/**
 * Simple request deduplication utility
 * Prevents duplicate API calls within a short timeframe
 */
import logger from './logger';

// Track in-flight requests to prevent duplicates
const requestPromises = new Map();
const lastFetchTimes = new Map();

/**
 * Normalize URL to ensure consistent cache keys
 * @param {string} url - URL to normalize
 * @param {Object} params - Query parameters
 * @returns {string} - Normalized URL for cache key
 */
const normalizeUrl = (url, params) => {
  // Create a unique key for this request including query params
  let key = url;
  if (params) {
    // Sort params to ensure consistent keys regardless of param order
    const sortedParams = Object.keys(params).sort().map(k => `${k}=${params[k]}`);
    key += `?${sortedParams.join('&')}`;
  }
  return key;
};

/**
 * Wraps an API call function to prevent duplicate requests
 * @param {Function} apiFn - The API call function to wrap
 * @param {number} dedupWindow - Time window in ms to deduplicate requests
 * @returns {Function} - Wrapped function that prevents duplicates
 */
export const dedupRequest = (apiFn, dedupWindow = 300) => {
  return async (...args) => {
    // Create a unique key for this request based on function name and args
    const fnName = apiFn.name || 'anonymousFunction';
    const requestKey = `${fnName}:${JSON.stringify(args)}`;
    
    // Check if we have an in-flight request
    if (requestPromises.has(requestKey)) {
      logger.debug(`Deduplicating request: ${requestKey}`);
      return requestPromises.get(requestKey);
    }
    
    // Check if we've made this request recently
    const now = Date.now();
    const lastFetchTime = lastFetchTimes.get(requestKey) || 0;
    
    if (now - lastFetchTime < dedupWindow) {
      logger.debug(`Skipping duplicate request within ${dedupWindow}ms window: ${requestKey}`);
      // We still need to return a promise
      const cachedPromise = requestPromises.get(requestKey);
      if (cachedPromise) {
        return cachedPromise;
      }
    }
    
    // Make the actual API call and store the promise
    const promise = apiFn(...args);
    requestPromises.set(requestKey, promise);
    lastFetchTimes.set(requestKey, now);
    
    // Clean up after the request completes
    promise.finally(() => {
      setTimeout(() => {
        requestPromises.delete(requestKey);
      }, dedupWindow);
    });
    
    return promise;
  };
};

export default {
  dedupRequest
};
